import type { Block, BlockType, Section } from '@/api/courses';

import { blockHasContent } from './blockContent';
import { essayCanSave } from './openAnswerMin';
import { openItemsAnswerOk } from './openItems';

/**
 * Что нужно ответить, чтобы раздел урока закрылся.
 *
 * Вынесено из `StudentLessonPlayerPage.tsx`: проверки собирают только `*.test.ts`,
 * и правило внутри страницы не проверялось бы вовсе.
 */

/**
 * Задания, которые зовут `onAnswer`, — раздел «пройден», только когда они отвечены.
 * Явный список, а не шаблон: `word_` когда-то цеплял и `word_card_set` — словарь,
 * который никогда не отвечает, и раздел с карточкой слов не закрывался никогда.
 *
 * ⚠️ `audio_record` ДОБАВЛЕН 10.09.2026 (договор `empty-open-answers.md`). До этого
 * раздел «только запись голоса» закрывался сам, стоило его открыть. Сервер теперь
 * отвечает на такое `409 open_answers_missing` — без этой строки ученик застрял бы,
 * не понимая почему. Поэтому фронт с этим правилом выкатывается раньше сервера.
 */
export const ANSWERABLE_TYPES: ReadonlySet<BlockType> = new Set<BlockType>([
  'quiz_no_timer',
  'quiz_with_timer',
  'true_false_ng',
  'word_drag_to_gap',
  'word_input_gap',
  'word_form_select',
  'word_drag_to_image',
  'word_input_image',
  'word_form_image',
  'gap_fill',
  'table_fill',
  'sentence_order',
  'column_sort',
  'paragraph_order',
  'anagram',
  'matching',
  'essay',
  'open_items',
  'highlight',
  'audio_record',
]);

/** Открытые задания — ответ проверяет человек, а сервер не принимает пустой. */
export const OPEN_TASK_TYPES: ReadonlySet<BlockType> = new Set<BlockType>(['essay', 'audio_record', 'open_items']);

/**
 * Обязательные задания раздела. Пустые заготовки не требуются: ответить на них
 * нельзя, а раздел из-за них запирался навсегда — см. `blockHasContent`.
 */
export function requiredBlocks(section: Section): Block[] {
  return (section.blocks ?? []).filter((b) => ANSWERABLE_TYPES.has(b.type) && blockHasContent(b));
}

/** Открытые задания раздела — без пустых заготовок. */
export function openTaskBlocks(section: Section): Block[] {
  return (section.blocks ?? []).filter((b) => OPEN_TASK_TYPES.has(b.type) && blockHasContent(b));
}

export function sectionHasOpenTasks(section: Section): boolean {
  return openTaskBlocks(section).length > 0;
}

/**
 * Сервер отказался закрыть раздел (`409 open_answers_missing`) и назвал задания.
 * С них снимается «отвечено» — иначе раздел снова и снова считался бы
 * заполненным, а автозакрытие не перезапустилось бы, когда ученик ответит.
 */
export function applyOpenAnswersMissing(
  answered: Record<string, boolean>,
  blockIds: readonly string[],
): Record<string, boolean> {
  const next = { ...answered };
  for (const id of blockIds) next[id] = false;
  return next;
}

/**
 * Когда плеер сам отмечает раздел пройденным (автозакрытие).
 *
 * ⚠️ ПОСЛЕ 409 open_answers_missing РАЗДЕЛ ЖДЁТ НОВОГО ОТВЕТА (техдолг п. 162,
 * прод 11.09.2026). Раньше плеер после отказа снимал отметку «уже отправляли», а
 * разбор отказа переписывал `answered` — и эффект запускался снова. Если сервер
 * назвал задание, которого фронт не требует (эссе с пустым `prompt`), раздел
 * оставался «заполненным», и отметка уходила снова без паузы: до 17 запросов в
 * секунду. Очередь такую запись из себя убирает — повторял именно плеер.
 * Теперь повтор только после того, как ученик изменит ответ в этом разделе.
 *
 * Прочие отказы — как было: раздел выпускается и уйдёт при следующей перерисовке.
 *
 * Состояние изменяемое и живёт весь показ урока: перерисовку оно не вызывает.
 */
export function createSectionAutoComplete() {
  /** Отметка отправлена, ещё в пути или принята — второй раз не шлём. */
  const fired = new Set<string>();
  /** Сервер ответил 409 — ждём, пока ученик изменит ответ в разделе. */
  const waitingAnswer = new Set<string>();
  return {
    shouldFire: (sectionId: string) => !fired.has(sectionId),
    markFired: (sectionId: string) => {
      fired.add(sectionId);
    },
    markRejected: (sectionId: string, code: string | undefined) => {
      if (code === 'open_answers_missing') {
        fired.add(sectionId);
        waitingAnswer.add(sectionId);
        return;
      }
      fired.delete(sectionId);
    },
    /**
     * Сервер принял ответ ученика. Выпускает только раздел, ждущий после 409:
     * отметку, которая ещё в пути или уже принята, второй раз не шлём.
     */
    answerChanged: (sectionId: string | undefined) => {
      if (sectionId && waitingAnswer.delete(sectionId)) fired.delete(sectionId);
    },
  };
}

export type SectionAutoComplete = ReturnType<typeof createSectionAutoComplete>;

/** Разделы, которые пора отметить: просмотрены, обязательное отвечено, отметка ещё не уходила. */
export function sectionsToFire(
  sections: readonly Section[],
  viewed: ReadonlySet<string>,
  answered: Readonly<Record<string, boolean>>,
  auto: Pick<SectionAutoComplete, 'shouldFire'>,
): Section[] {
  return sections.filter(
    (s) => auto.shouldFire(s.id) && viewed.has(s.id) && requiredBlocks(s).every((b) => answered[b.id]),
  );
}

/** Сохранённый ответ так, как его отдаёт сервер: `attempts = 0` — служебный пульс, не ответ. */
export type SavedAnswer = { answer_data: string; attempts?: number };

/**
 * Открытые задания раздела без годного ответа — зеркало серверного правила
 * (`progress.open_blocks_without_valid_answer`, миграции 000017/000020/000021): нет ответа,
 * есть только пульс, в сочинении ни буквы и ни цифры, запись не лежит в `homework_audio`.
 *
 * ⚠️ ДЛИНУ И САМ ФАЙЛ ЗАПИСИ ФРОНТ НЕ ЗНАЕТ. Короткую запись назовёт только сервер отказом
 * `409 open_answers_missing` — этот путь остаётся как был.
 */
export function openBlocksWithoutValidAnswer(section: Section, saved: Readonly<Record<string, SavedAnswer>>): Block[] {
  return openTaskBlocks(section).filter((b) => {
    const a = saved[b.id];
    // Старый сервер `attempts` не присылает — строка тогда считается ответом, как и раньше.
    if (!a || (a.attempts ?? 1) <= 0) return true;
    if (b.type === 'essay') return !essayCanSave(a.answer_data, 0);
    if (b.type === 'audio_record') return !a.answer_data.includes('/files/o/homework_audio/');
    if (b.type === 'open_items') return !openItemsAnswerOk(b.content, a.answer_data);
    return false;
  });
}

/**
 * Разделы, которые держит одно открытое задание (26.09.2026, вариант В разбора
 * `docs/CHECK-2026-09-26-KHABIBULLA-ANSWERS.md`). Ученик прошёл 7 разделов из 8, в «Warm up»
 * ответил на тесты и не сдал сочинение — урок не закрылся, следующий не открылся, и никто ему
 * этого не сказал.
 *
 * Застрявший раздел: не отмечен пройденным, ученик в нём был (есть хоть одна строка, пусть
 * пульс), все прочие задания отвечены, а открытое — нет. Ученик посреди раздела сюда не
 * попадает: у него не отвечены и тесты. Домашка не рассматривается — её закрывает кнопка сдачи.
 */
export function stuckOnOpenTasks(
  sections: readonly Section[],
  completed: ReadonlySet<string>,
  saved: Readonly<Record<string, SavedAnswer>>,
): { section: Section; blocks: Block[] }[] {
  const out: { section: Section; blocks: Block[] }[] = [];
  for (const s of sections) {
    if (s.is_homework || completed.has(s.id)) continue;
    const blocks = openBlocksWithoutValidAnswer(s, saved);
    if (blocks.length === 0) continue;
    const visited = (s.blocks ?? []).some((b) => saved[b.id] !== undefined);
    const othersDone = requiredBlocks(s)
      .filter((b) => !OPEN_TASK_TYPES.has(b.type))
      .every((b) => {
        const a = saved[b.id];
        return a !== undefined && (a.attempts ?? 1) > 0;
      });
    if (visited && othersDone) out.push({ section: s, blocks });
  }
  return out;
}
